/**
 * Created by katarinababic on 11.6.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SubscribedEventEntry } from '../../Model/Coach/Schedule/SubscribedEventEntry';
import { SubscribableEntry } from '../../Model/Explore/SubscribableEntry';
import { useTrans } from '../../Store/System/LocalizationStore';
import { WeekDayEntrySubscribedEvent } from '../../Model/Coach/Schedule/WeekDayEntrySubcribedEvent';
import { useRootStore } from '../../Store/useRootStore';
import { useLocalized } from '../../Hooks/useLocalized';
import { useDeleteSubscribedEvent } from '../../Hooks/useEventEntryMutations';
import { StringUtils } from '../../Utils/StringUtils';
import { LinkUtils } from '../../Utils/LinkUtils';
import { WorkoutDayCardEventListItem } from './WorkoutDayCardEventListItem';
import { AlertModal } from '../../Components/AlertModal';
import { Theme } from '../../Theme/Theme';
import { useToast } from '../../Hooks/useToast';
import { useLanguage } from '../../Hooks/useLanguage';

function useGetDuration(event: SubscribedEventEntry, object?: SubscribableEntry): string | undefined {
  const { t } = useTrans();
  if (object) {
    return t('globals.timeMin', { value: object.dayjsDuration.asMinutes() }) as string;
  }
  return undefined;
}

export type WorkoutDayCardSubscribedEventProps = {
  entry: WeekDayEntrySubscribedEvent;
};

export const WorkoutDayCardSubscribedEvent: React.FC<WorkoutDayCardSubscribedEventProps> = observer(({ entry }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTrans();
  const deleteSubscribedEvent = useDeleteSubscribedEvent();
  const { language } = useLanguage();
  const eventName = entry.event.getName(language);
  const objectName = useLocalized(entry.event.object?.name);

  const [showAlert, setShowAlert] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');

  const object = entry.event.object;
  const name = eventName || entry.event.title || StringUtils.capitalize(entry.event.objectType || '');
  const title = name ? StringUtils.shortenString(name, 40) : '';

  const duration = useGetDuration(entry.event, object);

  const image = entry.event.image ? { uri: entry.event.image } : require('../../Assets/Images/logo.png');

  // Determine event type label
  const typeLabel: string = t(`coach.schedule.eventTypes.${entry.event.objectType || 'event'}`, {
    defaultValue: entry.event.objectType ?? '',
  }) as string;
  const subtitle = duration ? `${duration} ${typeLabel}` : typeLabel;

  const handlePress = React.useCallback(() => {
    const link = entry.event.link;
    if (link) {
      const parsed = LinkUtils.parseLink(link);
      if (LinkUtils.isInternalUrl(parsed)) {
        const path = parsed.replace(/^https?:\/\/*[^/]+/, '');
        navigate(path);
      } else if (LinkUtils.isExternalWebUrl(parsed)) {
        window.open(parsed, '_blank');
      }
    }
  }, [entry, navigate]);

  // Remove only this subscription occurrence (API deletes subscription)
  const removeEvent = React.useCallback(() => {
    return deleteSubscribedEvent.mutateAsync(entry.event.id);
  }, [deleteSubscribedEvent, entry]);

  // For subscriptions, remove all future occurrences (same as delete currently)
  const removeAllEvents = React.useCallback(() => {
    return deleteSubscribedEvent.mutateAsync(entry.event.id);
  }, [deleteSubscribedEvent, entry]);

  const handleLongPress = React.useCallback(() => {
    const titleVal = t('explore.scheduler.removeAlert.titleSession', {
      value: objectName,
    }) as string;
    const msgVal = t('explore.scheduler.removeAlert.messageSession', {
      value: objectName,
    }) as string;
    setAlertTitle(titleVal);
    setAlertMessage(msgVal);
    setShowAlert(true);
  }, [objectName, t]);

  // Format image source for CSS
  const bgImage = typeof image === 'object' && 'uri' in image ? `url(${image.uri})` : `url(${image})`;

  return (
    <React.Fragment>
      <WorkoutDayCardEventListItem
        title={title}
        subtitle={subtitle}
        onPress={handlePress}
        onLongPress={handleLongPress}
        imageSource={bgImage}
        RightColumnComponent={
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleLongPress();
            }}
            style={{ padding: 4, cursor: 'pointer' }}
            title={t('explore.scheduler.removeAlert.removeOnlyThisEvent') as string}
          >
            <i className="bi bi-trash" style={{ fontSize: 20, color: Theme.Colors.error }} />
          </div>
        }
      />
      <AlertModal
        isOpen={showAlert}
        title={alertTitle}
        body={alertMessage}
        secondaryActionText={t('explore.scheduler.removeAlert.removeAllEvents') as string}
        onSecondaryAction={() => {
          removeAllEvents().then((event) => {
            toast.showToast(t('explore.scheduler.eventRemoved', { defaultValue: 'Event removed' }) as string, 'error');
            window.dispatchEvent(new Event('scheduleChanged'));
          });
          setShowAlert(false);
        }}
        confirmText={t('explore.scheduler.removeAlert.removeOnlyThisEvent') as string}
        cancelText={t('globals.cancel') as string}
        onConfirm={() => {
          removeEvent().then((event) => {
            toast.showToast(t('explore.scheduler.eventRemoved', { defaultValue: 'Event removed' }) as string, 'error');
            window.dispatchEvent(new Event('scheduleChanged'));
          });
          setShowAlert(false);
        }}
        onCancel={() => setShowAlert(false)}
      />
    </React.Fragment>
  );
});
